/* eslint-disable */
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import {
  Wrench, X, CheckCircle, AlertTriangle, Clock, Plus, ChevronDown,
  ChevronUp, MessageSquare, Phone, Mail, Settings, History, RefreshCw, Camera,
} from 'lucide-react';

// ─── Colour system ─────────────────────────────────────────────────────────────
const C = {
  yellow: '#F7EC71', yellowLight: '#FEFCE8',
  orange: '#E4AC49', orangeLight: '#FDF3DC',
  green: '#7EA594', greenLight: '#EAF2EF',
  cream: '#F7F6EF', charcoal: '#2F3A42',
  navy: '#445366', mid: '#7A8A96', border: '#D8DEE3',
  skyBlue: '#BCD9E5', skyBlueLight: '#EAF4F8', skyText: '#3A8FA3',
  white: '#FFFFFF',
  red: '#DC3545', redLight: '#FFF0F0',
  amber: '#E07B00', amberLight: '#FFF3E0',
  purple: '#7C3AED', purpleLight: '#F3EEFF',
};

const VENUE_IDENTITY = {
  'Box Jordaan':  { bg: '#EDF3FC', text: '#2563EB', border: '#BAD4F5', short: 'Jordaan'  },
  'Box Plantage': { bg: '#C8E6D8', text: '#2D7A65', border: '#7EA594', short: 'Plantage' },
  'Sassys':       { bg: '#EDE0F7', text: '#6D28A0', border: '#6D28A0', short: 'Sassys'   },
  'Prep Kitchen': { bg: C.cream,   text: C.navy,    border: C.border,  short: 'Prep'     },
};

const VENUES = ['Box Jordaan', 'Box Plantage', 'Sassys', 'Prep Kitchen'];

const CATEGORIES = [
  { key: 'Equipment', color: C.skyText,  bg: C.skyBlueLight  },
  { key: 'Building',  color: C.amber,    bg: C.amberLight    },
  { key: 'Safety',    color: C.red,      bg: C.redLight      },
  { key: 'Other',     color: C.mid,      bg: C.cream         },
];

const EQUIP_TYPES = [
  { key: 'Fridge',   color: C.skyText,  bg: C.skyBlueLight  },
  { key: 'Freezer',  color: '#1E6B8A',  bg: '#D6EEF8'       },
  { key: 'Kitchen',  color: C.amber,    bg: C.amberLight    },
  { key: 'Bar',      color: C.purple,   bg: C.purpleLight   },
  { key: 'Other',    color: C.mid,      bg: C.cream         },
];

const CONDITIONS = [
  { key: 'new',       label: 'New',       color: C.green,   bg: C.greenLight  },
  { key: 'good',      label: 'Good',      color: C.skyText, bg: C.skyBlueLight},
  { key: 'fair',      label: 'Fair',      color: C.amber,   bg: C.amberLight  },
  { key: 'last_legs', label: 'Last Legs', color: C.red,     bg: C.redLight    },
];

const STATUS_CFG = {
  open:        { label: 'Open',        color: C.amber,   bg: C.amberLight   },
  in_progress: { label: 'In Progress', color: C.skyText, bg: C.skyBlueLight },
  resolved:    { label: 'Resolved',    color: C.green,   bg: C.greenLight   },
};

const PRIORITY_CFG = {
  normal: { label: 'Normal', color: C.navy, bg: '#eef1f5' },
  urgent: { label: 'Urgent', color: C.red,  bg: C.redLight },
};

const AREAS = ['Restaurant', 'Kitchen', 'General'];

const EQUIP_STATUSES = [
  { key: 'active',     label: 'Active',     color: C.green,   bg: C.greenLight   },
  { key: 'in_storage', label: 'In Storage', color: C.amber,   bg: C.amberLight   },
  { key: 'retired',    label: 'Retired',    color: C.mid,     bg: C.cream        },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function useIsMobile() {
  const [m, setM] = useState(window.innerWidth < 768);
  useEffect(() => {
    const fn = () => setM(window.innerWidth < 768);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return m;
}

function formatDate(isoStr) {
  if (!isoStr) return '';
  return new Date(isoStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDateTime(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) +
    ' · ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function ageLabel(isoStr) {
  if (!isoStr) return '';
  const diff = Date.now() - new Date(isoStr).getTime();
  const h = Math.floor(diff / 36e5);
  const d = Math.floor(h / 24);
  if (d > 0) return `${d}d ago`;
  if (h > 0) return `${h}h ago`;
  return 'just now';
}

function daysUntilService(lastDate, intervalDays) {
  if (!lastDate || !intervalDays) return null;
  const next = new Date(lastDate);
  next.setDate(next.getDate() + intervalDays);
  const diff = Math.ceil((next - Date.now()) / 86400000);
  return diff;
}

function daysUntilExpiry(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - Date.now()) / 86400000);
}

// ─── Shared badges ────────────────────────────────────────────────────────────
function Badge({ label, color, bg, size = 'sm' }) {
  const fs = size === 'xs' ? '0.6rem' : '0.65rem';
  return (
    <span style={{
      fontSize: fs, fontWeight: 700, color, background: bg,
      padding: '0.12rem 0.5rem', borderRadius: '999px',
      textTransform: 'uppercase', letterSpacing: '0.04em',
      whiteSpace: 'nowrap',
    }}>{label}</span>
  );
}

function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.open;
  return <Badge label={cfg.label} color={cfg.color} bg={cfg.bg} />;
}

function PriorityBadge({ priority }) {
  const cfg = PRIORITY_CFG[priority] || PRIORITY_CFG.normal;
  return <Badge label={cfg.label} color={cfg.color} bg={cfg.bg} />;
}

function VenueBadge({ venue }) {
  const vi = VENUE_IDENTITY[venue];
  if (!vi) return null;
  return <Badge label={vi.short} color={vi.text} bg={vi.bg} />;
}

// ─── Venue dropdown ────────────────────────────────────────────────────────────
function VenueDropdown({ value, onChange }) {
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{
          padding: '0.38rem 2rem 0.38rem 0.75rem',
          borderRadius: '999px',
          border: `1.5px solid ${value !== 'All' ? C.navy : C.border}`,
          background: value !== 'All' ? C.navy : C.white,
          color: value !== 'All' ? C.white : C.charcoal,
          fontSize: '0.78rem', fontWeight: 600,
          cursor: 'pointer', appearance: 'none',
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='${value !== 'All' ? '%23ffffff' : '%237A8A96'}' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 0.55rem center',
        }}
      >
        <option value="All">All Venues</option>
        {VENUES.map(v => <option key={v} value={v}>{v}</option>)}
      </select>
    </div>
  );
}

// ─── Tab bar ──────────────────────────────────────────────────────────────────
function TabBar({ tabs, active, onChange }) {
  return (
    <div style={{ display: 'flex', borderBottom: `2px solid ${C.border}`, marginBottom: '1.25rem', gap: '0' }}>
      {tabs.map(tab => {
        const isActive = active === tab.key;
        return (
          <button
            key={tab.key}
            onClick={() => onChange(tab.key)}
            style={{
              padding: '0.6rem 1.1rem',
              background: 'none', border: 'none',
              borderBottom: isActive ? `2px solid ${C.navy}` : '2px solid transparent',
              marginBottom: '-2px',
              color: isActive ? C.navy : C.mid,
              fontSize: '0.85rem', fontWeight: isActive ? 700 : 500,
              cursor: 'pointer', transition: 'all 0.15s',
              display: 'flex', alignItems: 'center', gap: '0.35rem',
            }}
          >
            {tab.label}
            {tab.count != null && tab.count > 0 && (
              <span style={{
                background: isActive ? C.navy : C.border,
                color: isActive ? C.white : C.mid,
                borderRadius: '999px', fontSize: '0.65rem',
                fontWeight: 700, padding: '0.05rem 0.4rem',
                minWidth: '1.2rem', textAlign: 'center',
              }}>{tab.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ─── Category count-cards ─────────────────────────────────────────────────────
function CategoryCards({ categories, tasks, active, onChange, isMobile }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${categories.length + 1}, 1fr)`,
      gap: isMobile ? '0.35rem' : '0.5rem',
      marginBottom: '0.85rem',
    }}>
      {/* All card */}
      {(() => {
        const isActive = active === 'All';
        const count = tasks.length;
        return (
          <div key="All" onClick={() => onChange('All')} style={{
            padding: isMobile ? '0.45rem 0.3rem' : '0.65rem 0.75rem',
            background: isActive ? C.navy : C.white,
            borderRadius: '10px', cursor: 'pointer',
            border: `1.5px solid ${isActive ? C.navy : C.border}`,
            boxShadow: isActive ? `0 0 0 1px ${C.navy}` : '0 1px 3px rgba(0,0,0,0.05)',
            transition: 'all 0.15s', textAlign: 'center',
          }}>
            <div style={{
              fontSize: isMobile ? '0.55rem' : '0.62rem', fontWeight: 700,
              color: isActive ? 'rgba(255,255,255,0.8)' : C.mid,
              textTransform: 'uppercase', letterSpacing: '0.04em',
              marginBottom: '0.2rem', whiteSpace: 'nowrap',
              overflow: 'hidden', textOverflow: 'ellipsis',
            }}>All</div>
            <div style={{
              fontSize: isMobile ? '1.1rem' : '1.4rem', fontWeight: 900,
              color: isActive ? C.white : (count > 0 ? C.charcoal : C.border),
            }}>{count}</div>
          </div>
        );
      })()}
      {categories.map(cat => {
        const count = tasks.filter(t => t.category === cat.key).length;
        const isActive = active === cat.key;
        return (
          <div key={cat.key} onClick={() => onChange(isActive ? 'All' : cat.key)} style={{
            padding: isMobile ? '0.45rem 0.3rem' : '0.65rem 0.75rem',
            background: isActive ? cat.bg : (count > 0 ? cat.bg + '88' : C.white),
            borderRadius: '10px', cursor: 'pointer',
            border: `1.5px solid ${isActive ? cat.color : (count > 0 ? cat.color + '44' : C.border)}`,
            boxShadow: isActive ? `0 0 0 1.5px ${cat.color}` : '0 1px 3px rgba(0,0,0,0.05)',
            transition: 'all 0.15s', textAlign: 'center',
          }}>
            <div style={{
              fontSize: isMobile ? '0.5rem' : '0.62rem', fontWeight: 700,
              color: isActive ? cat.color : (count > 0 ? cat.color : C.mid),
              textTransform: 'uppercase', letterSpacing: '0.04em',
              marginBottom: '0.2rem', whiteSpace: 'nowrap',
              overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{cat.key}</div>
            <div style={{
              fontSize: isMobile ? '1.1rem' : '1.4rem', fontWeight: 900,
              color: isActive ? cat.color : (count > 0 ? cat.color : C.border),
            }}>{count}</div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Equipment type count-cards ───────────────────────────────────────────────
function TypeCards({ equipment, active, onChange, isMobile }) {
  const types = EQUIP_TYPES;
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${types.length + 1}, 1fr)`,
      gap: isMobile ? '0.35rem' : '0.5rem',
      marginBottom: '0.85rem',
    }}>
      {[{ key: 'All', color: C.navy, bg: C.skyBlueLight }, ...types].map(t => {
        const count = t.key === 'All' ? equipment.length : equipment.filter(e => e.type === t.key).length;
        const isActive = active === t.key;
        return (
          <div key={t.key} onClick={() => onChange(isActive && t.key !== 'All' ? 'All' : t.key)} style={{
            padding: isMobile ? '0.45rem 0.3rem' : '0.65rem 0.75rem',
            background: isActive ? (t.key === 'All' ? C.navy : t.bg) : (count > 0 ? t.bg + '66' : C.white),
            borderRadius: '10px', cursor: 'pointer',
            border: `1.5px solid ${isActive ? t.color : (count > 0 ? t.color + '44' : C.border)}`,
            boxShadow: isActive ? `0 0 0 1.5px ${t.color}` : '0 1px 3px rgba(0,0,0,0.05)',
            transition: 'all 0.15s', textAlign: 'center',
          }}>
            <div style={{
              fontSize: isMobile ? '0.5rem' : '0.6rem', fontWeight: 700,
              color: isActive ? (t.key === 'All' ? C.white : t.color) : (count > 0 ? t.color : C.mid),
              textTransform: 'uppercase', letterSpacing: '0.04em',
              marginBottom: '0.2rem', whiteSpace: 'nowrap',
              overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{t.key}</div>
            <div style={{
              fontSize: isMobile ? '1.1rem' : '1.4rem', fontWeight: 900,
              color: isActive ? (t.key === 'All' ? C.white : t.color) : (count > 0 ? t.color : C.border),
            }}>{count}</div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Task card ────────────────────────────────────────────────────────────────
function TaskCard({ task, onClick }) {
  const isInProgress = task.status === 'in_progress';
  const catCfg = CATEGORIES.find(c => c.key === task.category) || CATEGORIES[CATEGORIES.length - 1];
  return (
    <div
      onClick={onClick}
      style={{
        background: C.white, borderRadius: '11px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
        marginBottom: '0.5rem', cursor: 'pointer',
        transition: 'box-shadow 0.15s',
        borderLeft: `4px solid ${
          task.priority === 'urgent' ? C.red
          : isInProgress ? C.skyText
          : C.border
        }`,
        border: `1px solid ${C.border}`,
        borderLeft: `4px solid ${
          task.priority === 'urgent' ? C.red
          : isInProgress ? C.skyText
          : C.border
        }`,
      }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 3px 10px rgba(0,0,0,0.12)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.07)'}
    >
      <div style={{ padding: '0.8rem 1rem' }}>
        <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginBottom: '0.35rem', alignItems: 'center' }}>
          <PriorityBadge priority={task.priority} />
          <StatusBadge status={task.status} />
          <Badge label={task.category} color={catCfg.color} bg={catCfg.bg} />
          <VenueBadge venue={task.venue} />
        </div>
        <p style={{ margin: '0 0 0.2rem', fontWeight: 700, fontSize: '0.9rem', color: C.charcoal, lineHeight: 1.3 }}>
          {task.title}
        </p>
        {task.equipment_name && (
          <p style={{ margin: '0 0 0.2rem', fontSize: '0.75rem', color: C.skyText, fontWeight: 600 }}>
            🔧 {task.equipment_name}
          </p>
        )}
        {task.description && (
          <p style={{ margin: '0 0 0.2rem', fontSize: '0.78rem', color: C.mid, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {task.description}
          </p>
        )}
        <p style={{ margin: 0, fontSize: '0.7rem', color: C.mid }}>
          {task.reported_by || 'unknown'} · {ageLabel(task.reported_at)}
        </p>
      </div>
    </div>
  );
}

// ─── Task drawer ──────────────────────────────────────────────────────────────
function TaskDrawer({ task, currentUser, isManager, isAdmin, equipment, onClose, onUpdate }) {
  const [updates,      setUpdates]      = useState([]);
  const [newNote,      setNewNote]      = useState('');
  const [posting,      setPosting]      = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [resNote,      setResNote]      = useState(task.resolution_notes || '');
  const [showResNote,  setShowResNote]  = useState(false);
  const [editEquip,    setEditEquip]    = useState(false);
  const [newEquipId,   setNewEquipId]   = useState(task.equipment_id || '');

  // Admin edit mode
  const [editMode,      setEditMode]      = useState(false);
  const [editTitle,     setEditTitle]     = useState(task.title || '');
  const [editDesc,      setEditDesc]      = useState(task.description || '');
  const [editCategory,  setEditCategory]  = useState(task.category || 'Other');
  const [editEquipType, setEditEquipType] = useState('');
  const [editArea,      setEditArea]      = useState(task.area || 'General');
  const [editPriority,  setEditPriority]  = useState(task.priority || 'normal');
  const [editVenue,     setEditVenue]     = useState(task.venue || VENUES[0]);
  const [editEquipId,   setEditEquipId]   = useState(task.equipment_id || '');
  const [editSaving,    setEditSaving]    = useState(false);

  useEffect(() => {
    supabase.from('maintenance_task_updates').select('*')
      .eq('task_id', task.id).order('created_at', { ascending: true })
      .then(({ data }) => setUpdates(data || []));
  }, [task.id]);

  async function postUpdate() {
    if (!newNote.trim()) return;
    setPosting(true);
    const { data } = await supabase.from('maintenance_task_updates').insert({
      task_id: task.id, author: currentUser?.name || 'unknown', note: newNote.trim(),
    }).select().single();
    if (data) setUpdates(prev => [...prev, data]);
    setNewNote('');
    setPosting(false);
  }

  async function changeStatus(status) {
    setSaving(true);
    const patch = { status };
    if (status === 'resolved') {
      patch.resolved_by = currentUser?.name || 'unknown';
      patch.resolved_at = new Date().toISOString();
      patch.resolution_notes = resNote.trim() || null;
    } else {
      patch.resolved_by = null;
      patch.resolved_at = null;
    }
    const { data } = await supabase.from('maintenance_tasks')
      .update(patch).eq('id', task.id).select().single();
    if (data) onUpdate(data);
    setSaving(false);
  }

  async function changePriority(priority) {
    const { data } = await supabase.from('maintenance_tasks')
      .update({ priority }).eq('id', task.id).select().single();
    if (data) onUpdate(data);
  }

  async function saveEquipment() {
    const equip = equipment.find(e => e.id === newEquipId);
    const patch = {
      equipment_id: newEquipId || null,
      equipment_name: equip ? equip.name : null,
    };
    const { data } = await supabase.from('maintenance_tasks')
      .update(patch).eq('id', task.id).select().single();
    if (data) onUpdate(data);
    setEditEquip(false);
  }

  async function saveEditMode() {
    if (!editTitle.trim()) return;
    setEditSaving(true);
    const equip = equipment.find(e => e.id === editEquipId);
    const derivedArea = editCategory === 'Equipment' && equip ? equip.area : editArea;
    const patch = {
      title:          editTitle.trim(),
      description:    editDesc.trim() || null,
      category:       editCategory,
      area:           derivedArea,
      priority:       editPriority,
      venue:          editVenue,
      equipment_id:   editEquipId || null,
      equipment_name: equip ? equip.name : null,
    };
    const { data } = await supabase.from('maintenance_tasks')
      .update(patch).eq('id', task.id).select().single();
    if (data) onUpdate(data);
    setEditSaving(false);
    setEditMode(false);
  }

  const isResolved = task.status === 'resolved';
  const catCfg = CATEGORIES.find(c => c.key === task.category) || CATEGORIES[CATEGORIES.length - 1];

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1500, background: 'rgba(47,58,66,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ width: '100%', maxWidth: '480px', height: '100%', background: C.white, boxShadow: '-8px 0 32px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>

        {/* Header */}
        <div style={{ padding: '1.25rem 1.5rem', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.45rem' }}>
              <PriorityBadge priority={task.priority} />
              <StatusBadge status={task.status} />
              <Badge label={task.category} color={catCfg.color} bg={catCfg.bg} />
              <VenueBadge venue={task.venue} />
            </div>
            <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: C.charcoal, lineHeight: 1.3 }}>
              {task.title}
            </h3>
          </div>
          <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexShrink: 0 }}>
            {isAdmin && !editMode && (
              <button onClick={() => setEditMode(true)}
                style={{ padding: '0.28rem 0.7rem', background: C.cream, border: `1px solid ${C.border}`, borderRadius: '7px', fontSize: '0.72rem', fontWeight: 600, color: C.navy, cursor: 'pointer' }}>
                Edit
              </button>
            )}
            {editMode && (
              <button onClick={() => setEditMode(false)}
                style={{ padding: '0.28rem 0.7rem', background: C.cream, border: `1px solid ${C.border}`, borderRadius: '7px', fontSize: '0.72rem', color: C.mid, cursor: 'pointer' }}>
                Cancel
              </button>
            )}
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mid, padding: '0.1rem' }}>
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, padding: '1.25rem 1.5rem', overflowY: 'auto' }}>

          {/* ── EDIT MODE ── */}
          {editMode ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.25rem' }}>
              {/* Title */}
              <div>
                <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Title</label>
                <input value={editTitle} onChange={e => setEditTitle(e.target.value)}
                  style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.88rem', color: C.charcoal, boxSizing: 'border-box' }} />
              </div>
              {/* Description */}
              <div>
                <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Description</label>
                <textarea value={editDesc} onChange={e => setEditDesc(e.target.value)} rows={2}
                  style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.84rem', resize: 'vertical', boxSizing: 'border-box', color: C.charcoal }} />
              </div>
              {/* Category */}
              <div>
                <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Category</label>
                <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                  {CATEGORIES.map(cat => (
                    <button key={cat.key} onClick={() => setEditCategory(cat.key)}
                      style={{ padding: '0.28rem 0.65rem', borderRadius: '999px', border: `1.5px solid ${editCategory === cat.key ? cat.color : C.border}`, background: editCategory === cat.key ? cat.bg : C.white, color: editCategory === cat.key ? cat.color : C.mid, fontSize: '0.76rem', fontWeight: editCategory === cat.key ? 700 : 400, cursor: 'pointer' }}>
                      {cat.key}
                    </button>
                  ))}
                </div>
              </div>
              {/* Area — only for non-Equipment categories */}
              {editCategory !== 'Equipment' && (
                <div>
                  <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Area</label>
                  <div style={{ display: 'flex', gap: '0.35rem' }}>
                    {AREAS.map(a => (
                      <button key={a} onClick={() => setEditArea(a)}
                        style={{ padding: '0.28rem 0.65rem', borderRadius: '999px', border: `1.5px solid ${editArea === a ? C.navy : C.border}`, background: editArea === a ? C.navy : C.white, color: editArea === a ? C.white : C.mid, fontSize: '0.76rem', fontWeight: editArea === a ? 700 : 400, cursor: 'pointer' }}>
                        {a}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {/* Priority */}
              <div>
                <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Priority</label>
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  {Object.entries(PRIORITY_CFG).map(([key, cfg]) => (
                    <button key={key} onClick={() => setEditPriority(key)}
                      style={{ flex: 1, padding: '0.32rem 0', borderRadius: '7px', border: `2px solid ${editPriority === key ? cfg.color : C.border}`, background: editPriority === key ? cfg.bg : C.white, color: editPriority === key ? cfg.color : C.mid, fontSize: '0.8rem', fontWeight: editPriority === key ? 700 : 400, cursor: 'pointer' }}>
                      {cfg.label}
                    </button>
                  ))}
                </div>
              </div>
              {/* Venue */}
              <div>
                <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Venue</label>
                <select value={editVenue} onChange={e => { setEditVenue(e.target.value); setEditEquipId(''); }}
                  style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.85rem', color: C.charcoal, background: C.white }}>
                  {VENUES.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              {/* Equipment type + item — only for Equipment category */}
              {editCategory === 'Equipment' ? (
                <>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Equipment Type</label>
                    <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                      {EQUIP_TYPES.map(t => (
                        <button key={t.key} onClick={() => { setEditEquipType(editEquipType === t.key ? '' : t.key); setEditEquipId(''); }}
                          style={{ padding: '0.28rem 0.65rem', borderRadius: '999px', border: `1.5px solid ${editEquipType === t.key ? t.color : C.border}`, background: editEquipType === t.key ? t.bg : C.white, color: editEquipType === t.key ? t.color : C.mid, fontSize: '0.76rem', fontWeight: editEquipType === t.key ? 700 : 400, cursor: 'pointer' }}>
                          {t.key}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Equipment Item</label>
                    <select value={editEquipId} onChange={e => setEditEquipId(e.target.value)}
                      style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.85rem', color: C.charcoal, background: C.white }}>
                      <option value="">— None —</option>
                      {equipment
                        .filter(e => e.venue === editVenue && (!editEquipType || e.type === editEquipType))
                        .map(e => <option key={e.id} value={e.id}>{e.name}{e.area ? ` · ${e.area}` : ''}</option>)}
                    </select>
                  </div>
                </>
              ) : (
                <div>
                  <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Equipment Item</label>
                  <select value={editEquipId} onChange={e => setEditEquipId(e.target.value)}
                    style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.85rem', color: C.charcoal, background: C.white }}>
                    <option value="">— None —</option>
                    {equipment.filter(e => e.venue === editVenue).map(e => <option key={e.id} value={e.id}>{e.name}{e.area ? ` · ${e.area}` : ''}</option>)}
                  </select>
                </div>
              )}
              {/* Save */}
              <button onClick={saveEditMode} disabled={!editTitle.trim() || editSaving}
                style={{ padding: '0.55rem 1.5rem', background: editTitle.trim() ? C.navy : C.border, color: C.white, border: 'none', borderRadius: '9px', fontWeight: 700, fontSize: '0.88rem', cursor: editTitle.trim() ? 'pointer' : 'not-allowed' }}>
                {editSaving ? 'Saving…' : 'Save Changes'}
              </button>
              <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '1rem', marginTop: '0.25rem' }} />
            </div>
          ) : (
            <>
              {/* Description */}
              {task.description && (
                <div style={{ marginBottom: '1rem', padding: '0.75rem', background: C.cream, borderRadius: '10px', fontSize: '0.85rem', color: C.charcoal, lineHeight: 1.55 }}>
                  {task.description}
                </div>
              )}

              {/* Equipment link */}
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.3rem' }}>
                  Equipment Item
                </div>
                {!editEquip ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '0.85rem', color: task.equipment_name ? C.charcoal : C.mid }}>
                      {task.equipment_name || 'Not linked to equipment'}
                    </span>
                    {isAdmin && (
                      <button onClick={() => setEditEquip(true)} style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: '6px', padding: '0.15rem 0.5rem', fontSize: '0.7rem', color: C.mid, cursor: 'pointer' }}>
                        {task.equipment_name ? 'Change' : 'Link'}
                      </button>
                    )}
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                    <select value={newEquipId} onChange={e => setNewEquipId(e.target.value)}
                      style={{ flex: 1, padding: '0.38rem 0.6rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.83rem', color: C.charcoal, background: C.white }}>
                      <option value="">— None —</option>
                      {equipment.map(e => (
                        <option key={e.id} value={e.id}>{e.name} ({e.venue})</option>
                      ))}
                    </select>
                    <button onClick={saveEquipment} style={{ padding: '0.38rem 0.8rem', background: C.navy, color: C.white, border: 'none', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}>Save</button>
                    <button onClick={() => setEditEquip(false)} style={{ padding: '0.38rem 0.6rem', background: C.cream, color: C.mid, border: 'none', borderRadius: '8px', fontSize: '0.8rem', cursor: 'pointer' }}>Cancel</button>
                  </div>
                )}
              </div>

              {/* Area + meta */}
              <div style={{ fontSize: '0.75rem', color: C.mid, marginBottom: '1.25rem', lineHeight: 1.7 }}>
                <span>Area: <strong style={{ color: C.charcoal }}>{task.area || '—'}</strong></span>
                {' · '}
                <span>Reported by <strong style={{ color: C.charcoal }}>{task.reported_by || '—'}</strong></span>
                {' · '}{formatDateTime(task.reported_at)}
                {isResolved && task.resolved_at && (
                  <span><br />Resolved by <strong style={{ color: C.charcoal }}>{task.resolved_by}</strong> · {formatDateTime(task.resolved_at)}</span>
                )}
              </div>
            </>
          )}

          {/* Resolution notes */}
          {/* Resolution notes + manager controls — hidden in edit mode */}
          {!editMode && (
            <>
              {isResolved && task.resolution_notes && (
                <div style={{ marginBottom: '1.25rem', padding: '0.75rem', background: C.greenLight, borderRadius: '10px', borderLeft: `3px solid ${C.green}` }}>
                  <p style={{ margin: '0 0 0.25rem', fontSize: '0.65rem', fontWeight: 700, color: C.green, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Resolution notes</p>
                  <p style={{ margin: 0, fontSize: '0.85rem', color: C.charcoal }}>{task.resolution_notes}</p>
                </div>
              )}

              {/* Manager controls */}
              {isManager && (
                <div style={{ marginBottom: '1.25rem', padding: '0.9rem', background: C.cream, borderRadius: '12px' }}>
                  <p style={{ margin: '0 0 0.6rem', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Manage</p>

                  {!isResolved && (
                    <div style={{ marginBottom: '0.65rem' }}>
                      <p style={{ margin: '0 0 0.3rem', fontSize: '0.72rem', color: C.mid }}>Priority</p>
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        {Object.entries(PRIORITY_CFG).map(([key, cfg]) => (
                          <button key={key} onClick={() => changePriority(key)}
                            style={{ flex: 1, padding: '0.3rem 0', borderRadius: '7px', border: `2px solid ${task.priority === key ? cfg.color : C.border}`, background: task.priority === key ? cfg.bg : C.white, color: task.priority === key ? cfg.color : C.mid, fontSize: '0.8rem', fontWeight: task.priority === key ? 700 : 400, cursor: 'pointer' }}>
                            {cfg.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                    {task.status !== 'open' && (
                      <button onClick={() => changeStatus('open')} disabled={saving}
                        style={{ padding: '0.35rem 0.75rem', borderRadius: '8px', border: `1px solid ${C.border}`, background: C.white, color: C.mid, fontSize: '0.78rem', cursor: 'pointer' }}>
                        Reopen
                      </button>
                    )}
                    {task.status === 'open' && (
                      <button onClick={() => changeStatus('in_progress')} disabled={saving}
                        style={{ padding: '0.35rem 0.75rem', borderRadius: '8px', border: `1px solid ${C.skyText}44`, background: C.skyBlueLight, color: C.skyText, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>
                        Mark in progress
                      </button>
                    )}
                    {task.status !== 'resolved' && (
                      <div style={{ width: '100%', marginTop: '0.4rem' }}>
                        <button onClick={() => setShowResNote(v => !v)}
                          style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.35rem 0.75rem', borderRadius: '8px', border: `1px solid ${C.green}44`, background: C.greenLight, color: C.green, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>
                          <CheckCircle size={13} />
                          Mark resolved
                          {showResNote ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                        </button>
                        {showResNote && (
                          <div style={{ marginTop: '0.5rem' }}>
                            <textarea value={resNote} onChange={e => setResNote(e.target.value)}
                              placeholder="Resolution notes (optional)..." rows={2}
                              style={{ width: '100%', padding: '0.45rem 0.6rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.82rem', resize: 'vertical', boxSizing: 'border-box', color: C.charcoal, marginBottom: '0.4rem' }} />
                            <button onClick={() => changeStatus('resolved')} disabled={saving}
                              style={{ padding: '0.35rem 1rem', background: C.green, color: C.white, border: 'none', borderRadius: '8px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer' }}>
                              {saving ? 'Saving…' : 'Confirm resolved'}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Update thread */}
          <div>
            <p style={{ margin: '0 0 0.65rem', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <MessageSquare size={11} /> Updates ({updates.length})
            </p>
            {updates.length === 0 && (
              <p style={{ fontSize: '0.82rem', color: C.mid, marginBottom: '0.75rem' }}>No updates yet.</p>
            )}
            {updates.map(u => (
              <div key={u.id} style={{ marginBottom: '0.6rem', padding: '0.6rem 0.75rem', background: C.cream, borderRadius: '9px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: C.mid, marginBottom: '0.2rem' }}>
                  <strong style={{ color: C.charcoal }}>{u.author}</strong>
                  <span>{formatDateTime(u.created_at)}</span>
                </div>
                <p style={{ margin: 0, fontSize: '0.83rem', color: C.charcoal, lineHeight: 1.5 }}>{u.note}</p>
              </div>
            ))}
            <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.5rem' }}>
              <input value={newNote} onChange={e => setNewNote(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && postUpdate()}
                placeholder="Add an update..."
                style={{ flex: 1, padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.83rem', color: C.charcoal }} />
              <button onClick={postUpdate} disabled={!newNote.trim() || posting}
                style={{ padding: '0.45rem 0.9rem', background: newNote.trim() ? C.navy : C.cream, color: newNote.trim() ? C.white : C.mid, border: 'none', borderRadius: '8px', fontSize: '0.83rem', fontWeight: 600, cursor: newNote.trim() ? 'pointer' : 'default' }}>
                {posting ? '…' : 'Post'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── New Task Form ─────────────────────────────────────────────────────────────
function NewTaskForm({ currentUser, equipment, onClose, onCreated }) {
  const isAdmin = currentUser?.role === 'admin';
  const defaultVenue = isAdmin ? VENUES[0] : (currentUser?.venue || VENUES[0]);

  const [venue,       setVenue]       = useState(defaultVenue);
  const [category,    setCategory]    = useState('');
  const [equipType,   setEquipType]   = useState('');
  const [area,        setArea]        = useState('General');
  const [priority,    setPriority]    = useState('normal');
  const [title,       setTitle]       = useState('');
  const [description, setDescription] = useState('');
  const [equipId,     setEquipId]     = useState('');
  const [submitting,  setSubmitting]  = useState(false);

  const isEquipment    = category === 'Equipment';
  const venueEquipment = equipment.filter(e => (e.status || 'active') === 'active' && e.venue === venue);
  const filteredEquip  = equipType
    ? venueEquipment.filter(e => e.type === equipType)
    : venueEquipment;

  function handleCategoryChange(cat) {
    setCategory(cat);
    setEquipType('');
    setEquipId('');
  }

  function handleEquipTypeChange(t) {
    setEquipType(t);
    setEquipId('');
  }

  async function handleSubmit() {
    if (!title.trim() || !category) return;
    setSubmitting(true);
    const equip = filteredEquip.find(e => e.id === equipId);
    const taskArea = isEquipment && equip ? equip.area : area;
    const { data } = await supabase.from('maintenance_tasks').insert({
      venue, category, priority,
      area: taskArea,
      title: title.trim(),
      description: description.trim() || null,
      equipment_id: equipId || null,
      equipment_name: equip ? equip.name : null,
      reported_by: currentUser?.name || 'unknown',
      reported_at: new Date().toISOString(),
      status: 'open',
    }).select().single();
    if (data) onCreated(data);
    setSubmitting(false);
    onClose();
  }

  const canSubmit = title.trim() && category;

  return (
    <div style={{ background: C.white, borderRadius: '14px', padding: '1.25rem', boxShadow: '0 2px 12px rgba(0,0,0,0.1)', marginBottom: '1.25rem', border: `1px solid ${C.border}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: 700, color: C.charcoal }}>Log New Task</p>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mid }}><X size={16} /></button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem', marginBottom: '0.65rem' }}>
        {/* Venue */}
        <div>
          <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Venue</label>
          <select value={venue} onChange={e => { setVenue(e.target.value); setEquipId(''); setEquipType(''); }}
            style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.85rem', color: C.charcoal, background: C.white }}>
            {(isAdmin ? VENUES : [currentUser?.venue]).map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        {/* Priority */}
        <div>
          <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Priority</label>
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            {Object.entries(PRIORITY_CFG).map(([key, cfg]) => (
              <button key={key} onClick={() => setPriority(key)}
                style={{ flex: 1, padding: '0.42rem 0', borderRadius: '7px', border: `2px solid ${priority === key ? cfg.color : C.border}`, background: priority === key ? cfg.bg : C.white, color: priority === key ? cfg.color : C.mid, fontSize: '0.8rem', fontWeight: priority === key ? 700 : 400, cursor: 'pointer' }}>
                {cfg.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Category */}
      <div style={{ marginBottom: '0.65rem' }}>
        <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Category *</label>
        <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
          {CATEGORIES.map(cat => (
            <button key={cat.key} onClick={() => handleCategoryChange(cat.key)}
              style={{ padding: '0.32rem 0.75rem', borderRadius: '999px', border: `1.5px solid ${category === cat.key ? cat.color : C.border}`, background: category === cat.key ? cat.bg : C.white, color: category === cat.key ? cat.color : C.mid, fontSize: '0.78rem', fontWeight: category === cat.key ? 700 : 400, cursor: 'pointer' }}>
              {cat.key}
            </button>
          ))}
        </div>
      </div>

      {/* Equipment flow — type pills then filtered dropdown */}
      {isEquipment && (
        <>
          <div style={{ marginBottom: '0.65rem' }}>
            <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Equipment Type</label>
            <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
              {EQUIP_TYPES.map(t => (
                <button key={t.key} onClick={() => handleEquipTypeChange(equipType === t.key ? '' : t.key)}
                  style={{ padding: '0.32rem 0.75rem', borderRadius: '999px', border: `1.5px solid ${equipType === t.key ? t.color : C.border}`, background: equipType === t.key ? t.bg : C.white, color: equipType === t.key ? t.color : C.mid, fontSize: '0.78rem', fontWeight: equipType === t.key ? 700 : 400, cursor: 'pointer' }}>
                  {t.key}
                </button>
              ))}
            </div>
          </div>
          <div style={{ marginBottom: '0.65rem' }}>
            <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>
              Equipment Item <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span>
            </label>
            {filteredEquip.length === 0 && equipType ? (
              <p style={{ fontSize: '0.78rem', color: C.mid, margin: '0.2rem 0 0', fontStyle: 'italic' }}>
                No {equipType} equipment registered at {venue}. Ask admin to add it, or leave unlinked.
              </p>
            ) : (
              <select value={equipId} onChange={e => setEquipId(e.target.value)}
                style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.85rem', color: C.charcoal, background: C.white }}>
                <option value="">— Not linked —</option>
                {filteredEquip.map(e => <option key={e.id} value={e.id}>{e.name}{e.area ? ` · ${e.area}` : ''}</option>)}
              </select>
            )}
          </div>
        </>
      )}

      {/* Area — only shown for non-Equipment categories */}
      {!isEquipment && (
        <div style={{ marginBottom: '0.65rem' }}>
          <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Area</label>
          <div style={{ display: 'flex', gap: '0.35rem' }}>
            {AREAS.map(a => (
              <button key={a} onClick={() => setArea(a)}
                style={{ padding: '0.32rem 0.75rem', borderRadius: '999px', border: `1.5px solid ${area === a ? C.navy : C.border}`, background: area === a ? C.navy : C.white, color: area === a ? C.white : C.mid, fontSize: '0.78rem', fontWeight: area === a ? 700 : 400, cursor: 'pointer' }}>
                {a}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Title */}
      <div style={{ marginBottom: '0.65rem' }}>
        <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Task *</label>
        <input value={title} onChange={e => setTitle(e.target.value)}
          placeholder="e.g. Dishwasher not draining"
          style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.88rem', color: C.charcoal, boxSizing: 'border-box' }} />
      </div>

      {/* Description */}
      <div style={{ marginBottom: '1rem' }}>
        <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Details (optional)</label>
        <textarea value={description} onChange={e => setDescription(e.target.value)}
          placeholder="Any additional context..." rows={2}
          style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.84rem', resize: 'vertical', boxSizing: 'border-box', color: C.charcoal }} />
      </div>

      <button onClick={handleSubmit} disabled={!canSubmit || submitting}
        style={{ padding: '0.55rem 1.5rem', background: canSubmit ? C.navy : C.border, color: C.white, border: 'none', borderRadius: '9px', fontWeight: 700, fontSize: '0.88rem', cursor: canSubmit ? 'pointer' : 'not-allowed' }}>
        {submitting ? 'Logging…' : 'Log Task'}
      </button>
    </div>
  );
}

// ─── Equipment card ────────────────────────────────────────────────────────────
function EquipmentCard({ item, taskCount, urgentCount, onClick }) {
  const typeCfg = EQUIP_TYPES.find(t => t.key === item.type) || EQUIP_TYPES[EQUIP_TYPES.length - 1];
  const condCfg = CONDITIONS.find(c => c.key === item.condition) || CONDITIONS[1];
  const daysLeft = daysUntilService(item.last_serviced_date, item.service_interval_days);
  const serviceDue = daysLeft != null && daysLeft <= 30;
  const serviceOverdue = daysLeft != null && daysLeft < 0;

  return (
    <div onClick={onClick} style={{
      background: C.white, borderRadius: '11px',
      border: `1px solid ${serviceOverdue ? C.red + '44' : serviceDue ? C.amber + '44' : C.border}`,
      padding: '0.9rem 1rem', cursor: 'pointer',
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      transition: 'box-shadow 0.15s',
      borderLeft: `4px solid ${urgentCount > 0 ? C.red : typeCfg.color}`,
    }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 3px 10px rgba(0,0,0,0.1)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)'}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginBottom: '0.3rem' }}>
            <Badge label={item.type} color={typeCfg.color} bg={typeCfg.bg} />
            <Badge label={condCfg.label} color={condCfg.color} bg={condCfg.bg} />
            <VenueBadge venue={item.venue} />
          </div>
          <p style={{ margin: '0 0 0.15rem', fontWeight: 700, fontSize: '0.9rem', color: C.charcoal }}>{item.name}</p>
          <p style={{ margin: 0, fontSize: '0.72rem', color: C.mid }}>{item.area}</p>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          {taskCount > 0 && (
            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: urgentCount > 0 ? C.red : C.amber, marginBottom: '0.2rem' }}>
              {urgentCount > 0 ? `⚠ ${urgentCount} urgent` : `${taskCount} open`}
            </div>
          )}
          {item.has_service_contract && daysLeft != null && (
            <div style={{ fontSize: '0.68rem', fontWeight: 700, color: serviceOverdue ? C.red : serviceDue ? C.amber : C.green }}>
              {serviceOverdue ? `Overdue ${Math.abs(daysLeft)}d` : `Service in ${daysLeft}d`}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Equipment Detail Drawer ───────────────────────────────────────────────────
function EquipmentDrawer({ item, currentUser, isManager, isAdmin, allTasks, suppliers, onClose, onUpdate }) {
  const [serviceLogs,   setServiceLogs]   = useState([]);
  const [showLogForm,   setShowLogForm]   = useState(false);
  const [serviceDate,   setServiceDate]   = useState(new Date().toISOString().split('T')[0]);
  const [servicedBy,    setServicedBy]    = useState('');
  const [serviceNotes,  setServiceNotes]  = useState('');
  const [saving,        setSaving]        = useState(false);

  // Edit mode
  const [editMode,        setEditMode]        = useState(false);
  const [editName,        setEditName]        = useState(item.name || '');
  const [editBrand,       setEditBrand]       = useState(item.brand || '');
  const [editType,        setEditType]        = useState(item.type || '');
  const [editArea,        setEditArea]        = useState(item.area || 'General');
  const [editVenue,       setEditVenue]       = useState(item.venue || VENUES[0]);
  const [editCondition,   setEditCondition]   = useState(item.condition || 'good');
  const [editStatus,      setEditStatus]      = useState(item.status || 'active');
  const [editSerial,      setEditSerial]      = useState(item.serial_number || '');
  const [editValue,       setEditValue]       = useState(item.purchase_value?.toString() || '');
  const [editPurchDate,   setEditPurchDate]   = useState(item.purchase_date || '');
  const [editWarranty,    setEditWarranty]    = useState(item.warranty_expiry || '');
  const [editContract,    setEditContract]    = useState(item.has_service_contract || false);
  const [editInterval,    setEditInterval]    = useState(item.service_interval_days?.toString() || '');
  const [editSupplier,    setEditSupplier]    = useState(item.supplier_id || '');
  const [editNotes,       setEditNotes]       = useState(item.notes || '');
  const [editPhotoFile,   setEditPhotoFile]   = useState(null);
  const [editPhotoPreview,setEditPhotoPreview]= useState(item.photo_url || null);
  const [editSaving,      setEditSaving]      = useState(false);

  const itemTasks  = allTasks.filter(t => t.equipment_id === item.id && t.status !== 'resolved');
  const typeCfg    = EQUIP_TYPES.find(t => t.key === item.type) || EQUIP_TYPES[EQUIP_TYPES.length - 1];
  const condCfg    = CONDITIONS.find(c => c.key === item.condition) || CONDITIONS[1];
  const statusCfg  = EQUIP_STATUSES.find(s => s.key === (item.status || 'active')) || EQUIP_STATUSES[0];
  const daysLeft   = daysUntilService(item.last_serviced_date, item.service_interval_days);
  const warrantyLeft = daysUntilExpiry(item.warranty_expiry);

  useEffect(() => {
    supabase.from('maintenance_service_log')
      .select('*')
      .eq('equipment_id', item.id)
      .order('serviced_date', { ascending: false })
      .then(({ data }) => setServiceLogs(data || []));
  }, [item.id]);

  function handleEditPhoto(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setEditPhotoFile(file);
    setEditPhotoPreview(URL.createObjectURL(file));
  }

  async function saveEdit() {
    if (!editName.trim() || !editType) return;
    setEditSaving(true);

    let photo_url = item.photo_url || null;
    if (editPhotoFile) {
      const ext  = editPhotoFile.name.split('.').pop();
      const path = `equipment/${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from('equipment-photos').upload(path, editPhotoFile);
      if (!upErr) {
        const { data: urlData } = supabase.storage.from('equipment-photos').getPublicUrl(path);
        photo_url = urlData?.publicUrl || null;
      }
    }

    const patch = {
      name:                  editName.trim(),
      brand:                 editBrand.trim() || null,
      type:                  editType,
      area:                  editArea,
      venue:                 editVenue,
      condition:             editCondition,
      status:                editStatus,
      serial_number:         editSerial.trim() || null,
      purchase_value:        editValue ? parseFloat(editValue) : null,
      purchase_date:         editPurchDate || null,
      warranty_expiry:       editWarranty || null,
      has_service_contract:  editContract,
      service_interval_days: editContract && editInterval ? parseInt(editInterval) : null,
      supplier_id:           editSupplier || null,
      notes:                 editNotes.trim() || null,
      photo_url,
    };
    const { data } = await supabase.from('maintenance_equipment')
      .update(patch).eq('id', item.id).select('*, maintenance_suppliers(*)').single();
    if (data) onUpdate(data);
    setEditSaving(false);
    setEditMode(false);
  }

  async function logService() {
    if (!serviceDate) return;
    setSaving(true);
    const { data: log } = await supabase.from('maintenance_service_log').insert({
      equipment_id: item.id,
      serviced_date: serviceDate,
      serviced_by: servicedBy.trim() || null,
      notes: serviceNotes.trim() || null,
      logged_by: currentUser?.name || 'unknown',
    }).select().single();

    await supabase.from('maintenance_equipment')
      .update({ last_serviced_date: serviceDate })
      .eq('id', item.id);

    if (log) setServiceLogs(prev => [log, ...prev]);
    onUpdate({ ...item, last_serviced_date: serviceDate });
    setShowLogForm(false);
    setSaving(false);
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1500, background: 'rgba(47,58,66,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ width: '100%', maxWidth: '480px', height: '100%', background: C.white, boxShadow: '-8px 0 32px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>

        {/* Header */}
        <div style={{ padding: '1.25rem 1.5rem', borderBottom: `1px solid ${C.border}`, background: C.navy }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.4rem' }}>
                <Badge label={item.type} color={typeCfg.bg} bg={typeCfg.color} />
                <Badge label={condCfg.label} color={condCfg.bg} bg={condCfg.color} />
                <Badge label={statusCfg.label} color={statusCfg.bg} bg={statusCfg.color} />
                <Badge label={item.venue} color={C.skyBlue} bg={C.navy} />
              </div>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, color: C.white }}>{item.name}</h3>
              <p style={{ margin: '0.2rem 0 0', fontSize: '0.8rem', color: C.skyBlue }}>{item.area}</p>
            </div>
            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
              {isAdmin && !editMode && (
                <button onClick={() => setEditMode(true)}
                  style={{ padding: '0.28rem 0.7rem', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '7px', fontSize: '0.72rem', fontWeight: 600, color: C.white, cursor: 'pointer' }}>
                  Edit
                </button>
              )}
              {editMode && (
                <button onClick={() => setEditMode(false)}
                  style={{ padding: '0.28rem 0.7rem', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '7px', fontSize: '0.72rem', color: 'rgba(255,255,255,0.7)', cursor: 'pointer' }}>
                  Cancel
                </button>
              )}
              <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px', cursor: 'pointer', color: C.white, padding: '0.3rem' }}>
                <X size={18} />
              </button>
            </div>
          </div>
        </div>

        <div style={{ flex: 1, padding: '1.25rem 1.5rem', overflowY: 'auto' }}>

          {/* ── EDIT MODE ── */}
          {editMode && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.25rem' }}>
              {/* Photo */}
              <div>
                <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Photo</label>
                {editPhotoPreview ? (
                  <div style={{ position: 'relative' }}>
                    <img src={editPhotoPreview} alt="equipment" style={{ width: '100%', maxHeight: '160px', objectFit: 'cover', borderRadius: '8px', border: `1px solid ${C.border}` }} />
                    <button onClick={() => { setEditPhotoFile(null); setEditPhotoPreview(null); }}
                      style={{ position: 'absolute', top: '6px', right: '6px', background: 'rgba(44,58,66,0.7)', border: 'none', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.white }}>
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.65rem', border: `1.5px dashed ${C.border}`, borderRadius: '8px', cursor: 'pointer', color: C.mid, fontSize: '0.82rem' }}>
                    <Camera size={15} /><span>Add photo</span>
                    <input type="file" accept="image/*" onChange={handleEditPhoto} style={{ display: 'none' }} />
                  </label>
                )}
              </div>
              {/* Name */}
              <div>
                <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Name</label>
                <input value={editName} onChange={e => setEditName(e.target.value)}
                  style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.88rem', color: C.charcoal, boxSizing: 'border-box' }} />
              </div>
              {/* Brand + Serial */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Brand</label>
                  <input value={editBrand} onChange={e => setEditBrand(e.target.value)} placeholder="e.g. Hobart"
                    style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.85rem', color: C.charcoal, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Serial No.</label>
                  <input value={editSerial} onChange={e => setEditSerial(e.target.value)} placeholder="Optional"
                    style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.85rem', color: C.charcoal, boxSizing: 'border-box' }} />
                </div>
              </div>
              {/* Venue + Area */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Venue</label>
                  <select value={editVenue} onChange={e => setEditVenue(e.target.value)}
                    style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.85rem', color: C.charcoal, background: C.white }}>
                    {VENUES.map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Area</label>
                  <select value={editArea} onChange={e => setEditArea(e.target.value)}
                    style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.85rem', color: C.charcoal, background: C.white }}>
                    {AREAS.map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
              </div>
              {/* Type */}
              <div>
                <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Type</label>
                <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                  {EQUIP_TYPES.map(t => (
                    <button key={t.key} onClick={() => setEditType(t.key)}
                      style={{ padding: '0.28rem 0.65rem', borderRadius: '999px', border: `1.5px solid ${editType === t.key ? t.color : C.border}`, background: editType === t.key ? t.bg : C.white, color: editType === t.key ? t.color : C.mid, fontSize: '0.76rem', fontWeight: editType === t.key ? 700 : 400, cursor: 'pointer' }}>
                      {t.key}
                    </button>
                  ))}
                </div>
              </div>
              {/* Condition */}
              <div>
                <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Condition</label>
                <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                  {CONDITIONS.map(c => (
                    <button key={c.key} onClick={() => setEditCondition(c.key)}
                      style={{ padding: '0.28rem 0.65rem', borderRadius: '999px', border: `1.5px solid ${editCondition === c.key ? c.color : C.border}`, background: editCondition === c.key ? c.bg : C.white, color: editCondition === c.key ? c.color : C.mid, fontSize: '0.76rem', fontWeight: editCondition === c.key ? 700 : 400, cursor: 'pointer' }}>
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
              {/* Status */}
              <div>
                <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Status</label>
                <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                  {EQUIP_STATUSES.map(s => (
                    <button key={s.key} onClick={() => setEditStatus(s.key)}
                      style={{ padding: '0.28rem 0.65rem', borderRadius: '999px', border: `1.5px solid ${editStatus === s.key ? s.color : C.border}`, background: editStatus === s.key ? s.bg : C.white, color: editStatus === s.key ? s.color : C.mid, fontSize: '0.76rem', fontWeight: editStatus === s.key ? 700 : 400, cursor: 'pointer' }}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
              {/* Financial */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.65rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Value (€)</label>
                  <input type="number" value={editValue} onChange={e => setEditValue(e.target.value)} placeholder="0"
                    style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.85rem', color: C.charcoal, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Purchased</label>
                  <input type="date" value={editPurchDate} onChange={e => setEditPurchDate(e.target.value)}
                    style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.85rem', color: C.charcoal, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Warranty</label>
                  <input type="date" value={editWarranty} onChange={e => setEditWarranty(e.target.value)}
                    style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.85rem', color: C.charcoal, boxSizing: 'border-box' }} />
                </div>
              </div>
              {/* Supplier */}
              <div>
                <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Supplier</label>
                <select value={editSupplier} onChange={e => setEditSupplier(e.target.value)}
                  style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.85rem', color: C.charcoal, background: C.white }}>
                  <option value="">— None —</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              {/* Contract */}
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.85rem', color: C.charcoal }}>
                  <input type="checkbox" checked={editContract} onChange={e => setEditContract(e.target.checked)} style={{ width: '16px', height: '16px' }} />
                  Has service contract
                </label>
                {editContract && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Interval (days)</label>
                    <input type="number" value={editInterval} onChange={e => setEditInterval(e.target.value)} placeholder="e.g. 90"
                      style={{ width: '140px', padding: '0.38rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.85rem', color: C.charcoal }} />
                  </div>
                )}
              </div>
              {/* Notes */}
              <div>
                <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Notes</label>
                <textarea value={editNotes} onChange={e => setEditNotes(e.target.value)} rows={2}
                  style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.84rem', resize: 'vertical', boxSizing: 'border-box', color: C.charcoal }} />
              </div>
              <button onClick={saveEdit} disabled={!editName.trim() || !editType || editSaving}
                style={{ padding: '0.55rem 1.5rem', background: editName.trim() && editType ? C.navy : C.border, color: C.white, border: 'none', borderRadius: '9px', fontWeight: 700, fontSize: '0.88rem', cursor: editName.trim() && editType ? 'pointer' : 'not-allowed' }}>
                {editSaving ? 'Saving…' : 'Save Changes'}
              </button>
              <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '1rem', marginTop: '0.25rem' }} />
            </div>
          )}

          {/* Supplier — visible to admin/manager only */}
          {!editMode && item.photo_url && (
            <div style={{ marginBottom: '1.25rem' }}>
              <img src={item.photo_url} alt={item.name} style={{ width: '100%', maxHeight: '200px', objectFit: 'cover', borderRadius: '10px', border: `1px solid ${C.border}` }} />
            </div>
          )}

          {/* Asset details */}
          {!editMode && (item.brand || item.serial_number || item.purchase_value || item.purchase_date || item.warranty_expiry) && (
            <div style={{ marginBottom: '1.25rem', padding: '0.9rem', background: C.cream, borderRadius: '12px' }}>
              <p style={{ margin: '0 0 0.5rem', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Asset Details</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.82rem', color: C.charcoal }}>
                {item.brand && <div><span style={{ color: C.mid }}>Brand: </span><strong>{item.brand}</strong></div>}
                {item.serial_number && <div><span style={{ color: C.mid }}>Serial: </span><strong style={{ fontFamily: 'monospace' }}>{item.serial_number}</strong></div>}
                {item.purchase_value && <div><span style={{ color: C.mid }}>Value: </span><strong>€{Number(item.purchase_value).toLocaleString('en-GB')}</strong></div>}
                {item.purchase_date && <div><span style={{ color: C.mid }}>Purchased: </span><strong>{formatDate(item.purchase_date)}</strong></div>}
                {item.warranty_expiry && (
                  <div style={{ marginTop: '0.2rem', padding: '0.4rem 0.65rem', borderRadius: '7px', background: warrantyLeft != null && warrantyLeft < 0 ? C.redLight : warrantyLeft != null && warrantyLeft <= 60 ? C.amberLight : C.greenLight, borderLeft: `3px solid ${warrantyLeft != null && warrantyLeft < 0 ? C.red : warrantyLeft != null && warrantyLeft <= 60 ? C.amber : C.green}` }}>
                    <span style={{ color: C.mid, fontSize: '0.78rem' }}>Warranty: </span>
                    <strong style={{ color: warrantyLeft != null && warrantyLeft < 0 ? C.red : warrantyLeft != null && warrantyLeft <= 60 ? C.amber : C.green }}>
                      {formatDate(item.warranty_expiry)}
                      {warrantyLeft != null && (warrantyLeft < 0 ? ` — expired ${Math.abs(warrantyLeft)}d ago` : warrantyLeft <= 60 ? ` — expires in ${warrantyLeft}d` : '')}
                    </strong>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Supplier — visible to admin/manager only */}
          {!editMode && isManager && item.maintenance_suppliers && (
            <div style={{ marginBottom: '1.25rem', padding: '0.9rem', background: C.cream, borderRadius: '12px' }}>
              <p style={{ margin: '0 0 0.5rem', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Supplier</p>
              <p style={{ margin: '0 0 0.2rem', fontSize: '0.9rem', fontWeight: 700, color: C.charcoal }}>{item.maintenance_suppliers.name}</p>
              {item.maintenance_suppliers.contact_name && (
                <p style={{ margin: '0 0 0.2rem', fontSize: '0.82rem', color: C.mid }}>{item.maintenance_suppliers.contact_name}</p>
              )}
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.4rem' }}>
                {item.maintenance_suppliers.phone && (
                  <a href={`tel:${item.maintenance_suppliers.phone}`} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.82rem', color: C.skyText, textDecoration: 'none', fontWeight: 600 }}>
                    <Phone size={13} /> {item.maintenance_suppliers.phone}
                  </a>
                )}
                {item.maintenance_suppliers.email && (
                  <a href={`mailto:${item.maintenance_suppliers.email}`} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.82rem', color: C.skyText, textDecoration: 'none', fontWeight: 600 }}>
                    <Mail size={13} /> Email
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Service schedule */}
          {item.has_service_contract && (
            <div style={{ marginBottom: '1.25rem', padding: '0.9rem', background: daysLeft != null && daysLeft < 0 ? C.redLight : daysLeft != null && daysLeft <= 30 ? C.amberLight : C.greenLight, borderRadius: '12px', borderLeft: `3px solid ${daysLeft != null && daysLeft < 0 ? C.red : daysLeft != null && daysLeft <= 30 ? C.amber : C.green}` }}>
              <p style={{ margin: '0 0 0.4rem', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Service Contract</p>
              <div style={{ fontSize: '0.85rem', color: C.charcoal, lineHeight: 1.6 }}>
                <div>Last serviced: <strong>{item.last_serviced_date ? formatDate(item.last_serviced_date) : 'Never'}</strong></div>
                <div>Interval: <strong>Every {item.service_interval_days} days</strong></div>
                {daysLeft != null && (
                  <div style={{ marginTop: '0.3rem', fontWeight: 700, color: daysLeft < 0 ? C.red : daysLeft <= 30 ? C.amber : C.green }}>
                    {daysLeft < 0 ? `⚠ Overdue by ${Math.abs(daysLeft)} days` : `Next service in ${daysLeft} days`}
                  </div>
                )}
              </div>
              {isManager && (
                <button onClick={() => setShowLogForm(v => !v)}
                  style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.38rem 0.9rem', background: C.navy, color: C.white, border: 'none', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}>
                  <RefreshCw size={13} /> Log Service
                </button>
              )}
              {showLogForm && (
                <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <input type="date" value={serviceDate} onChange={e => setServiceDate(e.target.value)}
                    style={{ padding: '0.38rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.84rem', color: C.charcoal }} />
                  <input value={servicedBy} onChange={e => setServicedBy(e.target.value)}
                    placeholder="Serviced by (company / person)"
                    style={{ padding: '0.38rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.84rem', color: C.charcoal }} />
                  <textarea value={serviceNotes} onChange={e => setServiceNotes(e.target.value)}
                    placeholder="Notes (optional)" rows={2}
                    style={{ padding: '0.38rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.84rem', resize: 'vertical', color: C.charcoal }} />
                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                    <button onClick={logService} disabled={saving}
                      style={{ padding: '0.38rem 1rem', background: C.green, color: C.white, border: 'none', borderRadius: '8px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer' }}>
                      {saving ? 'Saving…' : 'Confirm'}
                    </button>
                    <button onClick={() => setShowLogForm(false)}
                      style={{ padding: '0.38rem 0.75rem', background: C.cream, color: C.mid, border: 'none', borderRadius: '8px', fontSize: '0.82rem', cursor: 'pointer' }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Open tasks linked to this equipment */}
          <div style={{ marginBottom: '1.25rem' }}>
            <p style={{ margin: '0 0 0.6rem', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Open Tasks ({itemTasks.length})</p>
            {itemTasks.length === 0 ? (
              <p style={{ fontSize: '0.82rem', color: C.mid }}>No open tasks for this item.</p>
            ) : itemTasks.map(t => (
              <div key={t.id} style={{ marginBottom: '0.4rem', padding: '0.6rem 0.75rem', background: t.priority === 'urgent' ? C.redLight : C.cream, borderRadius: '9px', borderLeft: `3px solid ${t.priority === 'urgent' ? C.red : C.border}` }}>
                <div style={{ display: 'flex', gap: '0.3rem', marginBottom: '0.2rem' }}>
                  <PriorityBadge priority={t.priority} />
                  <StatusBadge status={t.status} />
                </div>
                <p style={{ margin: 0, fontSize: '0.84rem', fontWeight: 600, color: C.charcoal }}>{t.title}</p>
                <p style={{ margin: '0.1rem 0 0', fontSize: '0.7rem', color: C.mid }}>{ageLabel(t.reported_at)}</p>
              </div>
            ))}
          </div>

          {/* Service history */}
          <div>
            <p style={{ margin: '0 0 0.6rem', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <History size={11} /> Service History ({serviceLogs.length})
            </p>
            {serviceLogs.length === 0 ? (
              <p style={{ fontSize: '0.82rem', color: C.mid }}>No service logs yet.</p>
            ) : serviceLogs.map(log => (
              <div key={log.id} style={{ marginBottom: '0.5rem', padding: '0.6rem 0.75rem', background: C.cream, borderRadius: '9px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: C.mid }}>
                  <strong style={{ color: C.charcoal }}>{formatDate(log.serviced_date)}</strong>
                  <span>{log.logged_by}</span>
                </div>
                {log.serviced_by && <p style={{ margin: '0.15rem 0 0', fontSize: '0.78rem', color: C.charcoal }}>By: {log.serviced_by}</p>}
                {log.notes && <p style={{ margin: '0.1rem 0 0', fontSize: '0.78rem', color: C.mid }}>{log.notes}</p>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Add Equipment Form ────────────────────────────────────────────────────────
function AddEquipmentForm({ currentUser, suppliers, onClose, onCreated }) {
  const [name,          setName]          = useState('');
  const [brand,         setBrand]         = useState('');
  const [type,          setType]          = useState('');
  const [area,          setArea]          = useState('Kitchen');
  const [venue,         setVenue]         = useState(VENUES[0]);
  const [condition,     setCondition]     = useState('good');
  const [serialNumber,  setSerialNumber]  = useState('');
  const [purchaseValue, setPurchaseValue] = useState('');
  const [purchaseDate,  setPurchaseDate]  = useState('');
  const [warrantyExpiry,setWarrantyExpiry]= useState('');
  const [hasContract,   setHasContract]   = useState(false);
  const [intervalDays,  setIntervalDays]  = useState('');
  const [supplierId,    setSupplierId]    = useState('');
  const [notes,         setNotes]         = useState('');
  const [photoFile,     setPhotoFile]     = useState(null);
  const [photoPreview,  setPhotoPreview]  = useState(null);
  const [saving,        setSaving]        = useState(false);

  function handlePhotoChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  }

  async function handleSave() {
    if (!name.trim() || !type) return;
    setSaving(true);

    let photo_url = null;
    if (photoFile) {
      const ext  = photoFile.name.split('.').pop();
      const path = `equipment/${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from('equipment-photos').upload(path, photoFile);
      if (!upErr) {
        const { data: urlData } = supabase.storage.from('equipment-photos').getPublicUrl(path);
        photo_url = urlData?.publicUrl || null;
      }
    }

    const { data } = await supabase.from('maintenance_equipment').insert({
      name: name.trim(),
      brand: brand.trim() || null,
      type, area, venue, condition,
      serial_number:  serialNumber.trim() || null,
      purchase_value: purchaseValue ? parseFloat(purchaseValue) : null,
      purchase_date:  purchaseDate || null,
      warranty_expiry: warrantyExpiry || null,
      has_service_contract: hasContract,
      service_interval_days: hasContract && intervalDays ? parseInt(intervalDays) : null,
      supplier_id: supplierId || null,
      notes: notes.trim() || null,
      photo_url,
      active: true,
    }).select('*, maintenance_suppliers(*)').single();
    if (data) onCreated(data);
    setSaving(false);
    onClose();
  }

  const canSave = name.trim() && type;

  return (
    <div style={{ background: C.white, borderRadius: '14px', padding: '1.25rem', boxShadow: '0 2px 12px rgba(0,0,0,0.1)', marginBottom: '1.25rem', border: `1px solid ${C.border}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: 700, color: C.charcoal }}>Add Equipment</p>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mid }}><X size={16} /></button>
      </div>

      {/* Photo upload */}
      <div style={{ marginBottom: '0.9rem' }}>
        <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Photo</label>
        {photoPreview ? (
          <div style={{ position: 'relative', marginBottom: '0.4rem' }}>
            <img src={photoPreview} alt="preview" style={{ width: '100%', maxHeight: '160px', objectFit: 'cover', borderRadius: '8px', border: `1px solid ${C.border}` }} />
            <button onClick={() => { setPhotoFile(null); setPhotoPreview(null); }}
              style={{ position: 'absolute', top: '6px', right: '6px', background: 'rgba(44,58,66,0.7)', border: 'none', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.white }}>
              <X size={12} />
            </button>
          </div>
        ) : (
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.65rem', border: `1.5px dashed ${C.border}`, borderRadius: '8px', cursor: 'pointer', color: C.mid, fontSize: '0.82rem' }}>
            <Camera size={15} />
            <span>Add photo (optional)</span>
            <input type="file" accept="image/*" onChange={handlePhotoChange} style={{ display: 'none' }} />
          </label>
        )}
      </div>

      {/* Name + Brand */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem', marginBottom: '0.65rem' }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Name *</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Hobart Dishwasher"
            style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.88rem', color: C.charcoal, boxSizing: 'border-box' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Brand / Manufacturer</label>
          <input value={brand} onChange={e => setBrand(e.target.value)} placeholder="e.g. Hobart"
            style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.85rem', color: C.charcoal, boxSizing: 'border-box' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Serial Number</label>
          <input value={serialNumber} onChange={e => setSerialNumber(e.target.value)} placeholder="Optional"
            style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.85rem', color: C.charcoal, boxSizing: 'border-box' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Venue</label>
          <select value={venue} onChange={e => setVenue(e.target.value)}
            style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.85rem', color: C.charcoal, background: C.white }}>
            {VENUES.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Area</label>
          <select value={area} onChange={e => setArea(e.target.value)}
            style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.85rem', color: C.charcoal, background: C.white }}>
            {AREAS.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
      </div>

      {/* Type */}
      <div style={{ marginBottom: '0.65rem' }}>
        <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Type *</label>
        <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
          {EQUIP_TYPES.map(t => (
            <button key={t.key} onClick={() => setType(t.key)}
              style={{ padding: '0.32rem 0.75rem', borderRadius: '999px', border: `1.5px solid ${type === t.key ? t.color : C.border}`, background: type === t.key ? t.bg : C.white, color: type === t.key ? t.color : C.mid, fontSize: '0.78rem', fontWeight: type === t.key ? 700 : 400, cursor: 'pointer' }}>
              {t.key}
            </button>
          ))}
        </div>
      </div>

      {/* Condition */}
      <div style={{ marginBottom: '0.65rem' }}>
        <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Condition</label>
        <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
          {CONDITIONS.map(c => (
            <button key={c.key} onClick={() => setCondition(c.key)}
              style={{ padding: '0.32rem 0.75rem', borderRadius: '999px', border: `1.5px solid ${condition === c.key ? c.color : C.border}`, background: condition === c.key ? c.bg : C.white, color: condition === c.key ? c.color : C.mid, fontSize: '0.78rem', fontWeight: condition === c.key ? 700 : 400, cursor: 'pointer' }}>
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {/* Financial fields */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.65rem', marginBottom: '0.65rem' }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Value (€)</label>
          <input type="number" value={purchaseValue} onChange={e => setPurchaseValue(e.target.value)} placeholder="0"
            style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.85rem', color: C.charcoal, boxSizing: 'border-box' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Purchase Date</label>
          <input type="date" value={purchaseDate} onChange={e => setPurchaseDate(e.target.value)}
            style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.85rem', color: C.charcoal, boxSizing: 'border-box' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Warranty Expiry</label>
          <input type="date" value={warrantyExpiry} onChange={e => setWarrantyExpiry(e.target.value)}
            style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.85rem', color: C.charcoal, boxSizing: 'border-box' }} />
        </div>
      </div>

      {/* Supplier */}
      <div style={{ marginBottom: '0.65rem' }}>
        <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Supplier (who to call if it breaks)</label>
        <select value={supplierId} onChange={e => setSupplierId(e.target.value)}
          style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.85rem', color: C.charcoal, background: C.white }}>
          <option value="">— None —</option>
          {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>

      {/* Service contract */}
      <div style={{ marginBottom: '0.75rem' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.85rem', color: C.charcoal }}>
          <input type="checkbox" checked={hasContract} onChange={e => setHasContract(e.target.checked)} style={{ width: '16px', height: '16px' }} />
          Has service contract / scheduled maintenance
        </label>
        {hasContract && (
          <div style={{ marginTop: '0.5rem' }}>
            <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Service interval (days)</label>
            <input type="number" value={intervalDays} onChange={e => setIntervalDays(e.target.value)} placeholder="e.g. 90"
              style={{ width: '140px', padding: '0.38rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.85rem', color: C.charcoal }} />
          </div>
        )}
      </div>

      {/* Notes */}
      <div style={{ marginBottom: '1rem' }}>
        <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Notes (optional)</label>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Any additional notes..."
          style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.84rem', resize: 'vertical', boxSizing: 'border-box', color: C.charcoal }} />
      </div>

      <button onClick={handleSave} disabled={!canSave || saving}
        style={{ padding: '0.55rem 1.5rem', background: canSave ? C.navy : C.border, color: C.white, border: 'none', borderRadius: '9px', fontWeight: 700, fontSize: '0.88rem', cursor: canSave ? 'pointer' : 'not-allowed' }}>
        {saving ? 'Saving…' : 'Add Equipment'}
      </button>
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────
export default function MaintenancePage({ currentUser }) {
  const isManager = ['admin', 'manager'].includes(currentUser?.role);
  const isAdmin   = currentUser?.role === 'admin';
  const isMobile  = useIsMobile();

  const [activeTab,    setActiveTab]    = useState('open');
  const [tasks,        setTasks]        = useState([]);
  const [equipment,    setEquipment]    = useState([]);
  const [suppliers,    setSuppliers]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [equipLoading, setEquipLoading] = useState(true);

  // Warranty banner dismiss (session only)
  const [warrantyDismissed, setWarrantyDismissed] = useState(false);

  // Filters
  const [venueFilter,    setVenueFilter]    = useState('All');
  const [catFilter,      setCatFilter]      = useState('All');
  const [typeFilter,     setTypeFilter]     = useState('All');
  const [contractFilter, setContractFilter] = useState(false);
  const [equipStatus,    setEquipStatus]    = useState('active');
  const [sortOrder,      setSortOrder]      = useState('newest');

  // Selected items
  const [selectedTask,  setSelectedTask]  = useState(null);
  const [selectedEquip, setSelectedEquip] = useState(null);

  // Forms
  const [showNewTask,  setShowNewTask]  = useState(false);
  const [showAddEquip, setShowAddEquip] = useState(false);

  // Load tasks
  useEffect(() => {
    async function load() {
      setLoading(true);
      let q = supabase.from('maintenance_tasks')
        .select('*')
        .order('reported_at', { ascending: sortOrder === 'oldest' })
        .limit(500);
      if (venueFilter !== 'All') q = q.eq('venue', venueFilter);
      const { data } = await q;
      setTasks(data || []);
      setLoading(false);
    }
    load();
  }, [venueFilter, sortOrder]);

  // Load equipment
  useEffect(() => {
    async function load() {
      setEquipLoading(true);
      let q = supabase.from('maintenance_equipment')
        .select('*, maintenance_suppliers(*)')
        .order('name');
      if (venueFilter !== 'All') q = q.eq('venue', venueFilter);
      const { data } = await q;
      setEquipment(data || []);
      setEquipLoading(false);
    }
    load();
  }, [venueFilter]);

  // Load suppliers (for forms)
  useEffect(() => {
    supabase.from('maintenance_suppliers').select('*').eq('active', true).order('name')
      .then(({ data }) => setSuppliers(data || []));
  }, []);

  function updateTask(updated) {
    setTasks(prev => prev.map(t => t.id === updated.id ? updated : t));
    setSelectedTask(updated);
  }

  function updateEquipment(updated) {
    setEquipment(prev => prev.map(e => e.id === updated.id ? { ...e, ...updated } : e));
    setSelectedEquip(prev => prev ? { ...prev, ...updated } : prev);
  }

  // Derived task lists
  const openTasks   = tasks.filter(t => t.status !== 'resolved');
  const urgentTasks = tasks.filter(t => t.priority === 'urgent' && t.status !== 'resolved');

  function applyTaskFilters(list) {
    let filtered = [...list];
    if (catFilter !== 'All') filtered = filtered.filter(t => t.category === catFilter);
    return filtered;
  }

  const displayedTasks = applyTaskFilters(activeTab === 'urgent' ? urgentTasks : openTasks);

  const displayedEquipment = equipment.filter(e => {
    if (typeFilter !== 'All' && e.type !== typeFilter) return false;
    if (contractFilter && !e.has_service_contract) return false;
    if ((e.status || 'active') !== equipStatus) return false;
    return true;
  });

  // For category card counts, use the current tab's base list before category filter
  const baseForCounts = activeTab === 'urgent' ? urgentTasks : openTasks;
  const filteredForCounts = venueFilter === 'All' ? baseForCounts : baseForCounts; // already venue-filtered at fetch

  const tabs = [
    { key: 'open',      label: 'Open',      count: openTasks.length   },
    { key: 'urgent',    label: 'Urgent',    count: urgentTasks.length },
    { key: 'equipment', label: 'Equipment', count: null               },
  ];

  return (
    <div style={{ padding: isMobile ? '1rem' : '1.5rem', maxWidth: '900px', margin: '0 auto' }}>

      {/* Drawers */}
      {selectedTask && (
        <TaskDrawer
          task={selectedTask} currentUser={currentUser}
          isManager={isManager} isAdmin={isAdmin}
          equipment={equipment}
          onClose={() => setSelectedTask(null)}
          onUpdate={updateTask}
        />
      )}
      {selectedEquip && (
        <EquipmentDrawer
          item={selectedEquip} currentUser={currentUser}
          isManager={isManager} isAdmin={isAdmin}
          allTasks={tasks}
          suppliers={suppliers}
          onClose={() => setSelectedEquip(null)}
          onUpdate={updateEquipment}
        />
      )}

      {/* Page header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <Wrench size={20} style={{ color: C.mid }} />
          <h2 style={{ margin: 0, fontSize: '1.35rem', fontWeight: 800, color: C.charcoal }}>Maintenance</h2>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {isAdmin && activeTab === 'equipment' && (
            <button onClick={() => setShowAddEquip(v => !v)}
              style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.45rem 0.9rem', background: showAddEquip ? C.cream : C.white, color: C.navy, border: `1.5px solid ${C.border}`, borderRadius: '9px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}>
              <Settings size={13} /> Add Equipment
            </button>
          )}
          {isManager && activeTab !== 'equipment' && (
            <button onClick={() => setShowNewTask(v => !v)}
              style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.45rem 0.9rem', background: C.navy, color: C.white, border: 'none', borderRadius: '9px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer' }}>
              <Plus size={14} /> New Task
            </button>
          )}
        </div>
      </div>

      {/* Tab bar */}
      <TabBar tabs={tabs} active={activeTab} onChange={tab => { setActiveTab(tab); setCatFilter('All'); setTypeFilter('All'); }} />

      {/* Warranty alert banner — dismissible, session only */}
      {!warrantyDismissed && (() => {
        const expiring = equipment.filter(e => {
          const d = daysUntilExpiry(e.warranty_expiry);
          return d != null && d <= 60;
        });
        if (!expiring.length) return null;
        return (
          <div style={{ marginBottom: '1rem', padding: '0.75rem 1rem', background: C.amberLight, borderRadius: '10px', border: `1px solid ${C.amber}44`, display: 'flex', alignItems: 'flex-start', gap: '0.6rem' }}>
            <AlertTriangle size={15} style={{ color: C.amber, flexShrink: 0, marginTop: '1px' }} />
            <div style={{ flex: 1 }}>
              <p style={{ margin: '0 0 0.2rem', fontSize: '0.82rem', fontWeight: 700, color: C.amber }}>
                Warranty expiring soon — {expiring.length} item{expiring.length !== 1 ? 's' : ''}
              </p>
              <div style={{ fontSize: '0.78rem', color: C.charcoal }}>
                {expiring.map((e, i) => {
                  const d = daysUntilExpiry(e.warranty_expiry);
                  return (
                    <span key={e.id}>
                      {e.name} <span style={{ color: d < 0 ? C.red : C.amber }}>({d < 0 ? `expired ${Math.abs(d)}d ago` : `${d}d left`})</span>
                      {i < expiring.length - 1 ? ' · ' : ''}
                    </span>
                  );
                })}
              </div>
            </div>
            <button onClick={() => setWarrantyDismissed(true)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.amber, padding: '0.1rem', flexShrink: 0 }}>
              <X size={15} />
            </button>
          </div>
        );
      })()}

      {/* Forms */}
      {showNewTask && activeTab !== 'equipment' && (
        <NewTaskForm
          currentUser={currentUser} equipment={equipment}
          onClose={() => setShowNewTask(false)}
          onCreated={task => { setTasks(prev => [task, ...prev]); setShowNewTask(false); }}
        />
      )}
      {showAddEquip && activeTab === 'equipment' && isAdmin && (
        <AddEquipmentForm
          currentUser={currentUser} suppliers={suppliers}
          onClose={() => setShowAddEquip(false)}
          onCreated={item => { setEquipment(prev => [item, ...prev]); setShowAddEquip(false); }}
        />
      )}

      {/* ── Open / Urgent tab filters ── */}
      {(activeTab === 'open' || activeTab === 'urgent') && (
        <>
          <CategoryCards
            categories={CATEGORIES}
            tasks={filteredForCounts}
            active={catFilter}
            onChange={setCatFilter}
            isMobile={isMobile}
          />
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1rem' }}>
            <VenueDropdown value={venueFilter} onChange={setVenueFilter} />
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <select value={sortOrder} onChange={e => setSortOrder(e.target.value)}
                style={{ padding: '0.38rem 2rem 0.38rem 0.75rem', borderRadius: '999px', border: `1.5px solid ${C.border}`, background: C.white, color: C.charcoal, fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer', appearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%237A8A96' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.55rem center' }}>
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
              </select>
            </div>
          </div>
        </>
      )}

      {/* ── Equipment tab filters ── */}
      {activeTab === 'equipment' && (
        <>
          <TypeCards
            equipment={equipment}
            active={typeFilter}
            onChange={setTypeFilter}
            isMobile={isMobile}
          />
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1rem' }}>
            <VenueDropdown value={venueFilter} onChange={setVenueFilter} />
            {/* Equipment status dropdown */}
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <select value={equipStatus} onChange={e => setEquipStatus(e.target.value)}
                style={{
                  padding: '0.38rem 2rem 0.38rem 0.75rem',
                  borderRadius: '999px',
                  border: `1.5px solid ${equipStatus !== 'active' ? C.amber : C.border}`,
                  background: equipStatus !== 'active' ? C.amberLight : C.white,
                  color: equipStatus !== 'active' ? C.amber : C.charcoal,
                  fontSize: '0.78rem', fontWeight: 600,
                  cursor: 'pointer', appearance: 'none',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%237A8A96' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.55rem center',
                }}>
                {EQUIP_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <button
              onClick={() => setContractFilter(v => !v)}
              style={{ padding: '0.38rem 0.9rem', borderRadius: '999px', border: `1.5px solid ${contractFilter ? C.green : C.border}`, background: contractFilter ? C.greenLight : C.white, color: contractFilter ? C.green : C.mid, fontSize: '0.78rem', fontWeight: contractFilter ? 700 : 500, cursor: 'pointer' }}>
              {contractFilter ? '✓ ' : ''}Contracted only
            </button>
          </div>
        </>
      )}

      {/* ── Task list ── */}
      {(activeTab === 'open' || activeTab === 'urgent') && (
        loading ? (
          <p style={{ color: C.mid, fontSize: '0.85rem' }}>Loading…</p>
        ) : displayedTasks.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem 1rem', color: C.mid }}>
            <Wrench size={32} style={{ opacity: 0.2, marginBottom: '0.75rem' }} />
            <p style={{ fontSize: '0.9rem' }}>
              {activeTab === 'urgent' ? 'No urgent tasks.' : 'No open tasks.'}
            </p>
          </div>
        ) : (
          displayedTasks.map(task => (
            <TaskCard key={task.id} task={task} onClick={() => setSelectedTask(task)} />
          ))
        )
      )}

      {/* ── Equipment list ── */}
      {activeTab === 'equipment' && (
        equipLoading ? (
          <p style={{ color: C.mid, fontSize: '0.85rem' }}>Loading…</p>
        ) : displayedEquipment.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem 1rem', color: C.mid }}>
            <Settings size={32} style={{ opacity: 0.2, marginBottom: '0.75rem' }} />
            <p style={{ fontSize: '0.9rem' }}>No equipment found.</p>
            {isAdmin && <p style={{ fontSize: '0.82rem' }}>Use "Add Equipment" to register your first item.</p>}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {displayedEquipment.map(item => {
              const itemTasks = tasks.filter(t => t.equipment_id === item.id && t.status !== 'resolved');
              const urgentCount = itemTasks.filter(t => t.priority === 'urgent').length;
              return (
                <EquipmentCard
                  key={item.id} item={item}
                  taskCount={itemTasks.length}
                  urgentCount={urgentCount}
                  onClick={() => setSelectedEquip(item)}
                />
              );
            })}
          </div>
        )
      )}
    </div>
  );
}

// ─── Exported: MaintenanceSubmitSection (used in Checklist.js) ────────────────
export function MaintenanceSubmitSection({ venue, currentUser, onNavigate }) {
  const [openTasks,   setOpenTasks]   = useState([]);
  const [equipment,   setEquipment]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [showForm,    setShowForm]    = useState(false);
  const [category,    setCategory]    = useState('');
  const [equipType,   setEquipType]   = useState('');
  const [area,        setArea]        = useState('General');
  const [priority,    setPriority]    = useState('normal');
  const [title,       setTitle]       = useState('');
  const [description, setDescription] = useState('');
  const [equipId,     setEquipId]     = useState('');
  const [submitting,  setSubmitting]  = useState(false);
  const [done,        setDone]        = useState(false);

  useEffect(() => {
    Promise.all([
      supabase.from('maintenance_tasks').select('id,title,priority,status,reported_by,reported_at')
        .eq('venue', venue).neq('status', 'resolved').order('reported_at', { ascending: false }),
      supabase.from('maintenance_equipment').select('id,name,type,area').eq('venue', venue).eq('active', true).order('name'),
    ]).then(([{ data: tasks }, { data: equip }]) => {
      setOpenTasks(tasks || []);
      setEquipment(equip || []);
      setLoading(false);
    });
  }, [venue]);

  async function handleSubmit() {
    if (!title.trim() || !category) return;
    setSubmitting(true);
    const filteredEquip = category === 'Equipment'
      ? equipment.filter(e => !equipType || e.type === equipType)
      : equipment;
    const equip = filteredEquip.find(e => e.id === equipId);
    const taskArea = category === 'Equipment' && equip ? equip.area : area;
    const { data } = await supabase.from('maintenance_tasks').insert({
      venue, category, priority,
      area: taskArea,
      title: title.trim(),
      description: description.trim() || null,
      equipment_id: equipId || null,
      equipment_name: equip ? equip.name : null,
      reported_by: currentUser?.name || null,
      reported_at: new Date().toISOString(),
      status: 'open',
    }).select().single();
    if (data) setOpenTasks(prev => [data, ...prev]);
    setDone(true);
    setSubmitting(false);
  }

  if (done) {
    return (
      <div style={{ textAlign: 'center', padding: '1.5rem 1rem' }}>
        <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: C.greenLight, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 0.75rem' }}>
          <CheckCircle size={24} style={{ color: C.green }} />
        </div>
        <p style={{ fontWeight: 700, fontSize: '1rem', color: C.charcoal, margin: '0 0 0.3rem' }}>Task logged</p>
        <p style={{ fontSize: '0.82rem', color: C.mid, margin: '0 0 1.25rem' }}>Visible to managers.</p>
        {onNavigate && (
          <button onClick={() => onNavigate('maintenance')}
            style={{ padding: '0.48rem 1.15rem', background: C.navy, color: C.white, border: 'none', borderRadius: '8px', fontWeight: 600, fontSize: '0.84rem', cursor: 'pointer' }}>
            View all tasks
          </button>
        )}
      </div>
    );
  }

  return (
    <div>
      {/* Existing open tasks */}
      {!loading && openTasks.length > 0 && (
        <div style={{ marginBottom: '1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.6rem' }}>
            <AlertTriangle size={13} style={{ color: C.amber }} />
            <p style={{ margin: 0, fontSize: '0.72rem', fontWeight: 700, color: C.amber }}>
              {openTasks.length} open task{openTasks.length !== 1 ? 's' : ''} at {venue} — already reported?
            </p>
          </div>
          {openTasks.map(task => (
            <div key={task.id} style={{ background: C.cream, borderRadius: '10px', padding: '0.65rem 0.85rem', marginBottom: '0.4rem', border: `1px solid ${task.priority === 'urgent' ? C.red + '44' : C.border}` }}>
              <div style={{ display: 'flex', gap: '0.35rem', marginBottom: '0.2rem' }}>
                <PriorityBadge priority={task.priority} />
                <StatusBadge status={task.status} />
              </div>
              <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: 600, color: C.charcoal }}>{task.title}</p>
              <p style={{ margin: '0.1rem 0 0', fontSize: '0.72rem', color: C.mid }}>{ageLabel(task.reported_at)} · {task.reported_by || 'unknown'}</p>
            </div>
          ))}
        </div>
      )}

      {/* Log form */}
      {!showForm ? (
        <button onClick={() => setShowForm(true)}
          style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', width: '100%', padding: '0.6rem 0.9rem', border: `1.5px dashed ${C.border}`, borderRadius: '10px', background: C.white, color: C.mid, fontSize: '0.85rem', cursor: 'pointer', justifyContent: 'center' }}>
          <Plus size={14} /> Report a new task
        </button>
      ) : (
        <div style={{ background: C.white, borderRadius: '12px', padding: '1rem', border: `1px solid ${C.border}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.85rem' }}>
            <p style={{ margin: 0, fontSize: '0.82rem', fontWeight: 700, color: C.charcoal }}>New task</p>
            <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mid }}><X size={14} /></button>
          </div>

          {/* Priority */}
          <div style={{ marginBottom: '0.65rem' }}>
            <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.3rem' }}>Priority</label>
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              {Object.entries(PRIORITY_CFG).map(([key, cfg]) => (
                <button key={key} onClick={() => setPriority(key)}
                  style={{ flex: 1, padding: '0.35rem 0', borderRadius: '8px', border: `2px solid ${priority === key ? cfg.color : C.border}`, background: priority === key ? cfg.bg : C.white, color: priority === key ? cfg.color : C.mid, fontSize: '0.82rem', fontWeight: priority === key ? 700 : 400, cursor: 'pointer' }}>
                  {cfg.label}
                </button>
              ))}
            </div>
          </div>

          {/* Category */}
          <div style={{ marginBottom: '0.65rem' }}>
            <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.3rem' }}>Category *</label>
            <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
              {CATEGORIES.map(cat => (
                <button key={cat.key} onClick={() => { setCategory(cat.key); setEquipType(''); setEquipId(''); }}
                  style={{ padding: '0.28rem 0.65rem', borderRadius: '999px', border: `1.5px solid ${category === cat.key ? cat.color : C.border}`, background: category === cat.key ? cat.bg : C.white, color: category === cat.key ? cat.color : C.mid, fontSize: '0.76rem', fontWeight: category === cat.key ? 700 : 400, cursor: 'pointer' }}>
                  {cat.key}
                </button>
              ))}
            </div>
          </div>

          {/* Equipment flow */}
          {category === 'Equipment' && (
            <>
              <div style={{ marginBottom: '0.65rem' }}>
                <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Equipment Type</label>
                <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                  {EQUIP_TYPES.map(t => (
                    <button key={t.key} onClick={() => { setEquipType(equipType === t.key ? '' : t.key); setEquipId(''); }}
                      style={{ padding: '0.28rem 0.65rem', borderRadius: '999px', border: `1.5px solid ${equipType === t.key ? t.color : C.border}`, background: equipType === t.key ? t.bg : C.white, color: equipType === t.key ? t.color : C.mid, fontSize: '0.76rem', fontWeight: equipType === t.key ? 700 : 400, cursor: 'pointer' }}>
                      {t.key}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: '0.65rem' }}>
                <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>
                  Equipment Item <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span>
                </label>
                {equipment.filter(e => !equipType || e.type === equipType).length === 0 && equipType ? (
                  <p style={{ fontSize: '0.78rem', color: C.mid, margin: 0, fontStyle: 'italic' }}>
                    No {equipType} equipment at {venue}. Leave unlinked or contact admin.
                  </p>
                ) : (
                  <select value={equipId} onChange={e => setEquipId(e.target.value)}
                    style={{ width: '100%', padding: '0.42rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.84rem', color: C.charcoal, background: C.white }}>
                    <option value="">— Not linked —</option>
                    {equipment
                      .filter(e => !equipType || e.type === equipType)
                      .map(e => <option key={e.id} value={e.id}>{e.name}{e.area ? ` · ${e.area}` : ''}</option>)}
                  </select>
                )}
              </div>
            </>
          )}

          {/* Area — only for non-Equipment */}
          {category !== 'Equipment' && category !== '' && (
            <div style={{ marginBottom: '0.65rem' }}>
              <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.3rem' }}>Area</label>
              <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                {AREAS.map(a => (
                  <button key={a} onClick={() => setArea(a)}
                    style={{ padding: '0.28rem 0.65rem', borderRadius: '999px', border: `1.5px solid ${area === a ? C.navy : C.border}`, background: area === a ? C.navy : C.white, color: area === a ? C.white : C.mid, fontSize: '0.76rem', fontWeight: area === a ? 700 : 400, cursor: 'pointer' }}>
                    {a}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Title */}
          <div style={{ marginBottom: '0.65rem' }}>
            <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Task *</label>
            <input value={title} onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Dishwasher not draining"
              style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.88rem', color: C.charcoal, boxSizing: 'border-box' }} />
          </div>

          {/* Description */}
          <div style={{ marginBottom: '0.9rem' }}>
            <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Details (optional)</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)}
              placeholder="Any additional context..." rows={2}
              style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.84rem', resize: 'vertical', boxSizing: 'border-box', color: C.charcoal }} />
          </div>

          <button onClick={handleSubmit} disabled={!title.trim() || !category || submitting}
            style={{ width: '100%', padding: '0.55rem', background: title.trim() && category ? C.navy : C.border, color: C.white, border: 'none', borderRadius: '9px', fontWeight: 700, fontSize: '0.88rem', cursor: title.trim() && category ? 'pointer' : 'not-allowed' }}>
            {submitting ? 'Logging…' : 'Log Task'}
          </button>
        </div>
      )}
    </div>
  );
}